/** RFC 9457 problem details, as the API returns them. */
export interface ProblemDetails {
  title?: string;
  detail?: string;
  status?: number;
  code?: string;
  errors?: Record<string, string[]>;
}

/** Field-level validation errors, keyed by the field name the API used. */
export function fieldErrors(error: unknown): Record<string, string[]> {
  const problem = error as ProblemDetails | undefined;
  return problem?.errors ?? {};
}

/**
 * A message worth showing a person. Prefers the specific field complaint over the generic title,
 * because "Password must be at least 12 characters" beats "One or more fields are invalid".
 */
export function errorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  if (!error) {
    return fallback;
  }

  const problem = error as ProblemDetails;
  const firstField = Object.values(problem.errors ?? {})[0]?.[0];

  return firstField ?? problem.detail ?? problem.title ?? fallback;
}

/** Problem-details `code`, used to tell apart failures that share a status. */
export function errorCode(error: unknown): string | undefined {
  return (error as ProblemDetails | undefined)?.code;
}

/**
 * True when the API refused because of billing rather than permission or absence.
 *
 * Worth naming: these arrive as 402 on routes that otherwise answer 404, and without recognising
 * them a mutation would surface "Something went wrong" for a situation with an obvious next step.
 */
export function isBillingRefusal(error: unknown): boolean {
  const code = errorCode(error);
  return code === 'TeamReadOnly' || code === 'SeatLimitReached';
}
